import { useImagesFastCache } from "@design-lab/entities/artwork/model/useImagesFastCache";
import { PATTERNS } from "@design-lab/shared/constants/constants";
import { useEditMode } from "@design-lab/shared/hooks/useEditMode";
import { Button } from "@design-lab/shared/ui/design-system/Button";
import { Modal } from "@design-lab/shared/ui/design-system/Modal";
import { MarkdownContent } from "@design-lab/shared/ui/MarkdownContent";
import { Loader2, ShoppingCart, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useReviewPinningModal, type CompletedSessionInfo } from "../model/useReviewPinningModal";

type SessionCardProps = {
  session: CompletedSessionInfo;
  onDelete: (session: CompletedSessionInfo) => void;
  isDeleting: boolean;
  isDisabled: boolean;
};

function SessionCard({ session, onDelete, isDeleting, isDisabled }: SessionCardProps) {
  const [cachedImage] = useImagesFastCache([session.artworkImageUrl]);
  const imageSrc = cachedImage?.base64 || session.artworkImageUrl;

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-blue-200 bg-blue-50/30 p-4 sm:flex-row sm:gap-4">
      {/* Image - centered at top on mobile, right side on desktop */}
      {imageSrc && (
        <div
          className="flex h-28 w-28 shrink-0 items-center justify-center self-center overflow-hidden rounded border border-gray-200 sm:order-2 sm:h-32 sm:w-32 sm:self-auto"
          style={{ backgroundImage: `url(${PATTERNS.CHECKERED_SVG})` }}
        >
          <img
            src={imageSrc}
            alt={`${session.side} artwork`}
            className="h-full w-full object-contain"
          />
        </div>
      )}
      {/* Text content */}
      <div className="flex flex-1 flex-col justify-between sm:order-1">
        {session.summary ? (
          <div className="text-sm text-gray-700">
            <MarkdownContent content={session.summary} />
          </div>
        ) : (
          <p className="text-sm text-gray-700">No summary available</p>
        )}
        <div className="mt-3 flex gap-2">
          <Button
            data-testid={`pins-review-delete-${session.side}`}
            type="button"
            className="shrink-0 cursor-pointer rounded-lg border-2 border-red-400 bg-transparent px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
            onPress={() => onDelete(session)}
            isDisabled={isDeleting || isDisabled}
          >
            {isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function ReviewPinningModal() {
  const {
    isModalOpen,
    isAddingToCart,
    completedSessions,
    allApproved,
    closeModal,
    handleDelete,
    handleAddToCart
  } = useReviewPinningModal();
  const { isEditMode } = useEditMode();

  const [deletingSessionId, setDeletingSessionId] = useState<string | null>(null);
  const deleteTimeoutRef = useRef<number | null>(null);

  // Cleanup timeout on unmount to prevent state updates on unmounted component
  useEffect(
    () => () => {
      if (deleteTimeoutRef.current !== null) {
        clearTimeout(deleteTimeoutRef.current);
      }
    },
    []
  );

  const buttonText = isEditMode ? "SAVE CHANGES" : "ADD TO CART";
  const loadingText = isEditMode ? "SAVING..." : "ADDING...";

  const frontSessions = completedSessions.filter(s => s.side === "front");
  const backSessions = completedSessions.filter(s => s.side === "back");

  const onDelete = (session: CompletedSessionInfo) => {
    // Clear any existing timeout before setting a new one
    if (deleteTimeoutRef.current !== null) {
      clearTimeout(deleteTimeoutRef.current);
    }

    setDeletingSessionId(session.sessionId);
    handleDelete(session);
    // Reset after a short delay to allow for animation
    deleteTimeoutRef.current = window.setTimeout(() => setDeletingSessionId(null), 300);
  };

  if (!isModalOpen) {
    return null;
  }

  return (
    <Modal
      isOpen={isModalOpen}
      onClose={closeModal}
      closeOnEsc={!isAddingToCart}
      closeOnBackdropClick={!isAddingToCart}
      className="mx-auto max-h-[90vh] w-full max-w-[96%] overflow-hidden border-none shadow-none lg:max-w-xl"
      title={allApproved ? "Confirm Your Design" : "Review Design Request"}
      description={
        allApproved
          ? "Confirm your design before adding to cart"
          : "Review your pinning design requests before adding to cart"
      }
    >
      <div className="grid max-h-[90vh] grid-rows-[auto_minmax(0,1fr)_auto]">
        {/* Header */}
        <div className="relative flex items-center justify-center border-b p-4">
          <span className="text-lg font-semibold text-blue-700">
            {allApproved ? "Please confirm your design" : "Please review your design request"}
          </span>
          <button
            data-testid="pins-review-close"
            onClick={closeModal}
            disabled={isAddingToCart}
            className="absolute right-4 cursor-pointer text-gray-500 hover:text-gray-700 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className="sr-only">Close</span>
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="min-h-0 overflow-y-auto p-4">
          {completedSessions.length === 0 ? (
            <div className="flex min-h-[120px] items-center justify-center">
              <p className="text-sm text-gray-500">Design request removed</p>
            </div>
          ) : (
            <>
              {frontSessions.length > 0 && (
                <div className="mb-6">
                  <h3 className="mb-3 text-center text-base font-semibold text-gray-700">Front</h3>
                  <div className="space-y-3">
                    {frontSessions.map(session => (
                      <SessionCard
                        key={session.sessionId}
                        session={session}
                        onDelete={onDelete}
                        isDeleting={deletingSessionId === session.sessionId}
                        isDisabled={isAddingToCart}
                      />
                    ))}
                  </div>
                </div>
              )}

              {backSessions.length > 0 && (
                <div>
                  <h3 className="mb-3 text-center text-base font-semibold text-gray-700">Back</h3>
                  <div className="space-y-3">
                    {backSessions.map(session => (
                      <SessionCard
                        key={session.sessionId}
                        session={session}
                        onDelete={onDelete}
                        isDeleting={deletingSessionId === session.sessionId}
                        isDisabled={isAddingToCart}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end border-t p-4">
          <Button
            data-testid="pins-review-add-to-cart"
            variant="destructive"
            size="xl"
            className="px-6"
            onPress={handleAddToCart}
            isDisabled={isAddingToCart}
          >
            <ShoppingCart className="size-5" />
            {isAddingToCart ? loadingText : buttonText}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
